// =============================================================================
// BP-05-03: Calendar Frontend - AppointmentCard Component
// =============================================================================

import React from 'react';
import { format } from 'date-fns';
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  Home,
  Phone,
  Stethoscope,
  Heart,
  GraduationCap,
  Scale,
  User,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertCircle,
  Repeat,
} from 'lucide-react';
import {
  type AppointmentSummary,
  type Appointment,
  AppointmentType,
  AppointmentStatus,
  LocationType,
  APPOINTMENT_TYPE_CONFIG,
  LOCATION_TYPE_CONFIG,
} from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface AppointmentCardProps {
  appointment: AppointmentSummary | Appointment;
  variant?: 'default' | 'compact' | 'detailed';
  showDate?: boolean;
  showActions?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onComplete?: () => void;
  className?: string;
}

// -----------------------------------------------------------------------------
// Icon Components
// -----------------------------------------------------------------------------

const typeIcons: Record<AppointmentType, React.ElementType> = {
  [AppointmentType.MEDICAL]: Stethoscope,
  [AppointmentType.THERAPY]: Heart,
  [AppointmentType.SCHOOL]: GraduationCap,
  [AppointmentType.LEGAL]: Scale,
  [AppointmentType.OTHER]: Calendar,
};

const locationIcons: Record<LocationType, React.ElementType> = {
  [LocationType.IN_PERSON]: MapPin,
  [LocationType.TELEHEALTH]: Video,
  [LocationType.HOME]: Home,
  [LocationType.PHONE]: Phone,
};

const statusConfig: Record<
  AppointmentStatus,
  { icon: React.ElementType; label: string; className: string }
> = {
  [AppointmentStatus.SCHEDULED]: {
    icon: Calendar,
    label: 'Scheduled',
    className: 'text-blue-600 bg-blue-100',
  },
  [AppointmentStatus.CONFIRMED]: {
    icon: CheckCircle,
    label: 'Confirmed',
    className: 'text-sage-dark bg-sage/20',
  },
  [AppointmentStatus.COMPLETED]: {
    icon: CheckCircle,
    label: 'Completed',
    className: 'text-green-600 bg-green-100',
  },
  [AppointmentStatus.CANCELLED]: {
    icon: XCircle,
    label: 'Cancelled',
    className: 'text-red-600 bg-red-100',
  },
  [AppointmentStatus.NO_SHOW]: {
    icon: AlertCircle,
    label: 'No Show',
    className: 'text-orange-600 bg-orange-100',
  },
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function AppointmentCard({
  appointment,
  variant = 'default',
  showDate = true,
  showActions = false,
  onClick,
  onEdit,
  onCancel,
  onComplete,
  className,
}: AppointmentCardProps) {
  const color = appointment.color || APPOINTMENT_TYPE_CONFIG[appointment.appointmentType].defaultColor;
  const TypeIcon = typeIcons[appointment.appointmentType];
  const LocationIcon = locationIcons[appointment.locationType];
  const statusInfo = statusConfig[appointment.status];
  const StatusIcon = statusInfo.icon;

  const startDate = new Date(appointment.startsAt);
  const endDate = new Date(appointment.endsAt);

  const isFullAppointment = 'isRecurring' in appointment;
  const isRecurring = isFullAppointment && (appointment as Appointment).isRecurring;

  // Compact variant for calendar cells
  if (variant === 'compact') {
    return (
      <div
        onClick={onClick}
        className={cn(
          'px-2 py-1 rounded cursor-pointer transition-all hover:shadow-sm',
          className
        )}
        style={{
          backgroundColor: `${color}20`,
          borderLeft: `3px solid ${color}`,
        }}
      >
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium truncate" style={{ color }}>
            {format(startDate, 'h:mm a')}
          </span>
          <span className="text-xs truncate text-navy">{appointment.title}</span>
        </div>
      </div>
    );
  }

  // Detailed variant for detail view
  if (variant === 'detailed') {
    const fullAppointment = appointment as Appointment;
    
    return (
      <div
        className={cn(
          'bg-white rounded-xl border border-warm-gray/10 overflow-hidden',
          className
        )}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b border-warm-gray/10"
          style={{ backgroundColor: `${color}10` }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${color}30` }}
              >
                <TypeIcon className="w-6 h-6" style={{ color }} />
              </div>
              <div>
                <h3 className="text-lg font-display font-semibold text-navy">
                  {appointment.title}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                      statusInfo.className
                    )}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {statusInfo.label}
                  </span>
                  {isRecurring && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-purple-600 bg-purple-100">
                      <Repeat className="w-3 h-3" />
                      Recurring
                    </span>
                  )}
                </div>
              </div>
            </div>
            {showActions && (
              <button className="p-2 hover:bg-cream rounded-lg transition-colors">
                <MoreVertical className="w-5 h-5 text-warm-gray" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-cream flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-sage-dark" />
            </div>
            <div>
              <div className="font-medium text-navy">
                {format(startDate, 'EEEE, MMMM d, yyyy')}
              </div>
              <div className="text-sm text-warm-gray">
                {appointment.isAllDay
                  ? 'All Day'
                  : `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`}
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-cream flex items-center justify-center flex-shrink-0">
              <LocationIcon className="w-5 h-5 text-sage-dark" />
            </div>
            <div>
              <div className="font-medium text-navy">
                {LOCATION_TYPE_CONFIG[appointment.locationType].label}
              </div>
              {isFullAppointment && fullAppointment.locationName && (
                <div className="text-sm text-warm-gray">
                  {fullAppointment.locationName}
                </div>
              )}
              {isFullAppointment && fullAppointment.locationAddress && (
                <div className="text-sm text-warm-gray">
                  {fullAppointment.locationAddress}
                </div>
              )}
              {isFullAppointment && fullAppointment.telehealthLink && (
                <a
                  href={fullAppointment.telehealthLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-sage hover:underline"
                >
                  Join Video Call
                </a>
              )}
            </div>
          </div>

          {/* Provider */}
          {appointment.providerName && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-cream flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-sage-dark" />
              </div>
              <div>
                <div className="font-medium text-navy">{appointment.providerName}</div>
                <div className="text-sm text-warm-gray">
                  {APPOINTMENT_TYPE_CONFIG[appointment.appointmentType].label} Provider
                </div>
              </div>
            </div>
          )}

          {/* Child */}
          {appointment.childName && (
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-cream flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <div className="font-medium text-navy">{appointment.childName}</div>
                <div className="text-sm text-warm-gray">Patient</div>
              </div>
            </div>
          )}

          {/* Notes */}
          {isFullAppointment && fullAppointment.notes && (
            <div className="pt-4 border-t border-warm-gray/10">
              <h4 className="text-sm font-medium text-navy mb-2">Notes</h4>
              <p className="text-sm text-warm-gray">{fullAppointment.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && appointment.status !== AppointmentStatus.CANCELLED && (
          <div className="px-6 py-4 bg-cream/50 border-t border-warm-gray/10 flex gap-2">
            {appointment.status !== AppointmentStatus.COMPLETED && (
              <>
                <button
                  onClick={onEdit}
                  className="flex-1 px-4 py-2 bg-white border border-warm-gray/20 text-navy rounded-lg hover:bg-cream transition-colors text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={onComplete}
                  className="flex-1 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm font-medium"
                >
                  Complete
                </button>
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white rounded-xl border border-warm-gray/10 p-4 cursor-pointer transition-all hover:shadow-medium hover:-translate-y-0.5',
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <TypeIcon className="w-5 h-5" style={{ color }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-navy truncate">{appointment.title}</h4>
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                statusInfo.className
              )}
            >
              {statusInfo.label}
            </span>
          </div>

          {/* Date & Time */}
          <div className="flex items-center gap-2 mt-1 text-sm text-warm-gray">
            {showDate && (
              <>
                <Calendar className="w-3.5 h-3.5" />
                <span>{format(startDate, 'MMM d')}</span>
                <span className="text-warm-gray/50">•</span>
              </>
            )}
            <Clock className="w-3.5 h-3.5" />
            <span>
              {appointment.isAllDay
                ? 'All Day'
                : `${format(startDate, 'h:mm a')} - ${format(endDate, 'h:mm a')}`}
            </span>
          </div>

          {/* Provider & Location */}
          <div className="flex items-center gap-3 mt-2 text-sm">
            {appointment.providerName && (
              <span className="flex items-center gap-1 text-navy">
                <User className="w-3.5 h-3.5 text-warm-gray" />
                {appointment.providerName}
              </span>
            )}
            <span className="flex items-center gap-1 text-warm-gray">
              <LocationIcon className="w-3.5 h-3.5" />
              {LOCATION_TYPE_CONFIG[appointment.locationType].label}
            </span>
          </div>

          {/* Child */}
          {appointment.childName && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cream rounded-full text-xs text-navy">
                <Heart className="w-3 h-3 text-terracotta" />
                {appointment.childName}
              </span>
            </div>
          )}

          {/* Recurring indicator */}
          {isRecurring && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                <Repeat className="w-3 h-3" />
                Recurring
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppointmentCard;
